<script lang="ts">
  import { createEventDispatcher, onMount } from "svelte";
  import CartWidgetNotice from "@retailer/svelte/CartWidget/v2/CartWidgetNotice.svelte";
  import CartWidgetGroupV2 from "@retailer/svelte/CartWidget/v2/CartWidgetGroupV2.svelte";
  import PickupLocationModal from "@retailer/svelte/Cart/Page/PickupOrDelivery/PickupLocationModal.svelte";
  import RequestItem from "@retailer/svelte/CartWidget/v2/middleware/RequestItem.svelte";
  import NationalAccountRequestApproval from "@retailer/svelte/CartWidget/v2/middleware/NationalAccountRequestApproval.svelte";
  import CartStore from "@retailer/svelte/stores/CartStore";
  import RetailerFulfillmentMethodsStore from "@retailer/svelte/stores/RetailerFulfillmentMethodsStore";
  import { InventoryItemStore } from "@retailer/svelte/CartWidget/v2/Stores/InventoryItemStore";
  import { pickupWarehousesStore } from "@retailer/svelte/stores/PickupWarehousesStore";
  import * as PickupWarehousesService from "@retailer/services/pickup_warehouses_service";
  import { EventTracking } from "@shared/services/event_tracking/events";
  import {
    sendCartTrackingEvents,
    sendClickTracking,
  } from "@retailer/svelte/CartWidget/v2/Services/CartEventTracking";
  import { CartWidgetTargetObjectBase } from "@shared/services/event_tracking/entities";
  import { stagedRolloutCheck } from "@shared/svelte/Modules/StagedRolloutCheck.svelte";
  import {
    shouldUseRetailerFulfillmentMethodForDistributor,
    hideFulfillmentMethodPickerForDistributor,
  } from "@retailer/services/fulfillment_methods_service";

  import type { BaseEntityClass } from "@shared/services/event_tracking/entities/base_entity_class";
  import type { Distributor } from "@shared/types/Distributor";
  import type { Notice } from "@retailer/svelte/CartWidget/v2/Services/CartItemRequestService";
  import type { Product } from "@shared/types/ProductLine";
  import type { RetailerFulfillmentMethod } from "@shared/types/RetailerFulfillmentMethod";

  export let distributor: Distributor;
  export let inventoryId: number;
  export let product: Product;
  export let trackingEntities: BaseEntityClass[] = [];
  export let loaded: boolean = false;

  const hasFullfillmentMethodsRollout = stagedRolloutCheck("marketplace__fulfillment_methods");
  const targetObjectTypeBase = CartWidgetTargetObjectBase.ProductDetailPage;

  let showPickupSelectionModal = false;

  const { trackClick } = EventTracking;

  const dispatch = createEventDispatcher();

  const trackCartChangeEvent = event => {
    sendCartTrackingEvents(event, trackingEntities, inventoryId);
  };

  const trackCartInteractionEvent = event => {
    sendClickTracking(targetObjectTypeBase, event, trackingEntities);
  };

  const onPickupClick = async () => {
    trackClick("check_pickup_availability", trackingEntities);

    await PickupWarehousesService.get(product.id, distributor.id);

    showPickupSelectionModal = true;
  };

  const onPickupSelection = async selectedPickupLocation => {
    const quantityToAdd = 1;
    const unitQuantity = widgetItem.sold_by.includes("units") ? quantityToAdd : 0;
    const caseQuantity = unitQuantity ? 0 : quantityToAdd;

    await InventoryItemStore.addToCart(inventoryId, {
      inventory_id: inventoryId,
      unit_quantity: unitQuantity,
      case_quantity: caseQuantity,
      warehouse_id: selectedPickupLocation.id,
    });

    await CartStore.load();

    closeModal();
  };

  const closeModal = () => {
    showPickupSelectionModal = false;
  };

  const filterInformationalNotices = (notices: Notice[]) => {
    if (!notices || notices?.some(notice => notice.level === "error")) return [];

    return notices.filter(notice => notice.level === "info" && notice.message);
  };

  function openFulfillmentMethodsDrawer(): void {
    dispatch("openDrawer", { sku: widgetItem?.sku });
  }

  onMount(() => {
    InventoryItemStore.fetchInventoryItem(inventoryId);
  });

  let fulfillmentMethod: RetailerFulfillmentMethod = null;
  let fulfillmentModel: string = null;
  $: {
    if (
      hideFulfillmentMethodPickerForDistributor(distributor?.id) &&
      fulfillmentModel === "pickup_only"
    ) {
      fulfillmentMethod = $RetailerFulfillmentMethodsStore[distributor?.id] || null;
    }
  }
  $: {
    if (
      shouldUseRetailerFulfillmentMethodForDistributor(
        distributor?.id,
        distributor?.uses_warehouse_pickup,
        hasFullfillmentMethodsRollout && distributor?.uses_wine_cellar,
      ) &&
      !hideFulfillmentMethodPickerForDistributor(distributor?.id)
    ) {
      fulfillmentMethod = $RetailerFulfillmentMethodsStore[distributor?.id] || null;
      InventoryItemStore.fetchInventoryItem(inventoryId);
    }
  }
  $: fulfillmentWarehouseId =
    fulfillmentMethod?.warehouse_id || widgetItem?.distributor_summary?.default_warehouse_id;
  $: fulfillmentModel = widgetItem?.distributor_summary?.fulfillment_model;
  $: widgetItem = $InventoryItemStore?.inventoryItems[inventoryId];
  $: informationalNotices = filterInformationalNotices(widgetItem?.notices);
  $: notices = widgetItem?.notices.filter(notice => notice.level !== "info" && notice.message);
  $: pickUpOptionsAvailable =
    widgetItem?.stock_info?.additional_action?.endsWith("pickup_available");
  $: showOtherAvailableLocations = pickUpOptionsAvailable && !widgetItem?.fulfillment_summary;
  $: needsAccountNumber = widgetItem?.stock_info?.additional_action?.endsWith("connect_account");
</script>

{#if loaded && widgetItem}
  <NationalAccountRequestApproval cartWidgetItem={widgetItem}>
    <RequestItem cartWidgetItem={widgetItem} {fulfillmentWarehouseId}>
      <div
        data-testid={`product-details-cart-widget-${inventoryId}`}
        class="fizz-stack-16"
        slot="cart-widget"
      >
        <div class="fizz-stack-16">
          {#each notices as notice}
            <CartWidgetNotice {notice} />
          {/each}
        </div>
        <div class:fizz-stack-8={showOtherAvailableLocations}>
          <CartWidgetGroupV2
            {fulfillmentMethod}
            {widgetItem}
            {fulfillmentWarehouseId}
            on:cartWidgetChange={trackCartChangeEvent}
            on:updateQuantity={trackCartInteractionEvent}
          />
        </div>
        {#if showOtherAvailableLocations}
          <div class="fizz-stack-8">
            {#if hasFullfillmentMethodsRollout && widgetItem?.distributor_summary?.fulfillment_model === "pickup_only"}
              <button
                on:click|preventDefault={widgetItem?.distributor_summary?.uses_wine_cellar
                  ? openFulfillmentMethodsDrawer
                  : onPickupClick}
                class="fizz-button fizz-full-width fizz-stack-8"
              >
                Check other locations
              </button>
            {:else if !hasFullfillmentMethodsRollout || (hasFullfillmentMethodsRollout && widgetItem?.distributor_summary?.fulfillment_model === "pickup_and_delivery" && !needsAccountNumber)}
              <button
                on:click|preventDefault={hasFullfillmentMethodsRollout &&
                widgetItem?.distributor_summary?.uses_wine_cellar
                  ? openFulfillmentMethodsDrawer
                  : onPickupClick}
                class="fizz-button fizz-full-width fizz-stack-8"
              >
                View more availability
              </button>
            {/if}
          </div>
        {/if}
        <div class="fizz-flex-row fizz-justify-end">
          {#each informationalNotices as notice}
            <CartWidgetNotice {notice} />
          {/each}
        </div>
      </div>
    </RequestItem>
  </NationalAccountRequestApproval>
{/if}

{#if showPickupSelectionModal && $pickupWarehousesStore.loaded}
  <PickupLocationModal
    selectedPickupLocationId={null}
    pickupOptions={$pickupWarehousesStore.pickupWarehouses}
    {distributor}
    onCancel={closeModal}
    {onPickupSelection}
    shouldShowLowStock={pickUpOptionsAvailable}
  />
{/if}
